import { Text, useTheme } from '@querodelivery/uni-react';
import { faCircleCheck, faCircleX } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Pagination } from '@quero-delivery/quero-components-web';
import styles from './deliverymen-by-mission-table.module.scss';
import { moneyLabel } from '@/Utils/FnUtils';
import { TableSkeleton } from '@/_components/skeletons/table-skeleton/table-skeleton';
import { RankingEmptyState } from '../ranking-empty-state/ranking-empty-state';
import type { DeliverymanByMission } from '../../types';

const RANKING_LIMIT = 10;

type DeliverymenRankingTableContentProps = {
  deliverymen: DeliverymanByMission[];
  isInitialLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  page: number;
  searchTerm: string;
  showPagination: boolean;
  totalCount: number;
  onPageChange: (page: number) => void;
  onNavigateToDeliverymanDetails: (deliverymanId: string) => void;
};

export const DeliverymenRankingTableContent = ({
  deliverymen,
  isInitialLoading,
  isError,
  isFetching,
  page,
  searchTerm,
  showPagination,
  totalCount,
  onPageChange,
  onNavigateToDeliverymanDetails,
}: DeliverymenRankingTableContentProps) => {
  const { color } = useTheme();

  if (isInitialLoading) {
    return (
      <div className={styles.stateContainer}>
        <div style={{ width: '100%' }}>
          <TableSkeleton columnCount={5} rowCount={5} rowHeight={48} />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.stateContainer}>
        <RankingEmptyState
          title="Não foi possível carregar o ranking"
          description="Tente novamente em alguns instantes."
        />
      </div>
    );
  }

  if (deliverymen.length === 0) {
    return (
      <div className={styles.stateContainer}>
        <RankingEmptyState
          title="Nenhum entregador encontrado"
          description={
            searchTerm.trim()
              ? 'Não encontramos entregadores com o nome buscado.'
              : 'Ainda não há entregadores participando desta missão.'
          }
        />
      </div>
    );
  }

  return (
    <>
      <div
        className={`${styles.tableWrapper} ${
          isFetching ? styles.isFetching : ''
        }`}
      >
        <table className={styles.table}>
          <thead>
            <tr className={styles.tableHeader}>
              <th>#</th>
              <th>Entregador</th>
              <th>Entregas</th>
              <th>Meta</th>
              <th>Prêmio</th>
            </tr>
          </thead>
          <tbody>
            {deliverymen.map((deliveryman, index) => {
              const rank = (page - 1) * RANKING_LIMIT + index + 1;

              return (
                <tr
                  key={deliveryman._id}
                  className={styles.tableRow}
                  onClick={() =>
                    onNavigateToDeliverymanDetails(deliveryman._id)
                  }
                >
                  <td>
                    <div className={styles.deliverymanIndex}>
                      <Text
                        tone="highlight"
                        fontSize="xs"
                        lineHeight="md"
                        fontWeight="bold"
                      >
                        {rank}
                      </Text>
                    </div>
                  </td>
                  <td>
                    <Text
                      lineHeight="sm"
                      tone="secondary"
                      fontSize="sm"
                      fontWeight="medium"
                    >
                      {deliveryman.name}
                    </Text>
                  </td>
                  <td>
                    <Text
                      lineHeight="sm"
                      tone="secondary"
                      fontSize="sm"
                      fontWeight="medium"
                    >
                      {deliveryman.deliveriesQuantity}
                    </Text>
                  </td>
                  <td>
                    <div
                      style={{
                        backgroundColor: deliveryman.reached
                          ? color['color-success-surface']
                          : color['color-danger-surface'],
                      }}
                      className={styles.deliverymanReachedContainer}
                    >
                      <FontAwesomeIcon
                        width={13}
                        height={13}
                        icon={deliveryman.reached ? faCircleCheck : faCircleX}
                        color={
                          deliveryman.reached
                            ? color['color-success-fill']
                            : color['color-danger-fill']
                        }
                      />
                      <Text
                        lineHeight="lg"
                        tone={deliveryman.reached ? 'success' : 'error'}
                        fontSize="xs"
                        fontWeight="semibold"
                      >
                        {deliveryman.reached ? 'Atingiu' : 'Não atingiu'}
                      </Text>
                    </div>
                  </td>
                  <td>
                    <Text
                      lineHeight="lg"
                      tone={deliveryman.reached ? 'highlight' : 'secondary'}
                      fontSize="sm"
                      fontWeight="semibold"
                    >
                      {deliveryman.reached
                        ? moneyLabel(deliveryman.rewardValue)
                        : '- - -'}
                    </Text>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showPagination && (
        <div className={styles.pagination}>
          <Pagination
            size="sm"
            limitPerPage={RANKING_LIMIT}
            currentPage={page}
            totalCount={totalCount}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </>
  );
};
